import { ClockIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Container } from '@/components/legacy-ui';
import { NoData } from '@/components/legacy-ui/NoData';
import { Tooltip } from '@/components/Tooltip';
import { Spinner } from '@/components/ui/spinner';
import { isAegisTaskActive } from '@/sections/agents/utils/index';

import { TaskItem } from './TaskItem';

interface TaskMonitoringTabProps {
  taskMonitoring: ReturnType<
    typeof import('@/sections/agents/hooks').useTaskMonitoring
  >;
  expandedTasks: Set<string>;
  setExpandedTasks: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Component for monitoring Aegis management tasks
 *
 * This component combines the original TaskMonitoringTab and its container,
 * managing task display and expansion state in a single component.
 *
 * Provides:
 * - Real-time view of management task status
 * - Expandable task details with guidance
 * - Task sorting by creation date (most recent first)
 * - Loading indicator for active tasks
 */
export const TaskMonitoringTab: React.FC<TaskMonitoringTabProps> = ({
  taskMonitoring,
  expandedTasks,
  setExpandedTasks,
}) => {
  const { tasks } = taskMonitoring;

  const toggleTaskExpansion = (taskKey: string) => {
    setExpandedTasks((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(taskKey)) {
        newSet.delete(taskKey);
      } else {
        newSet.add(taskKey);
      }
      return newSet;
    });
  };

  const hasActiveTasks =
    Array.isArray(tasks) && tasks.some(task => isAegisTaskActive(task.status));

  return (
    <div className="flex h-full flex-col gap-4" data-testid="tasks-container">
      <Container className="border-chariot-stroke bg-chariot-background-primary flex h-full flex-1 flex-col rounded-md border p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Recent Management Tasks
            </h3>
            <Tooltip title="View real-time task execution status and history for this endpoint.">
              <div className="text-chariot-text-secondary cursor-help">
                <InformationCircleIcon className="size-4" />
              </div>
            </Tooltip>
          </div>
          {hasActiveTasks && (
            <div className="text-chariot-text-secondary flex items-center gap-2 text-xs">
              <Spinner size="xs" tone="muted" />
              <span>Refreshing...</span>
            </div>
          )}
        </div>

        {Array.isArray(tasks) && tasks.length > 0 ? (
          <div className="space-y-3" role="list" aria-label="Management tasks">
            {tasks
              .sort((a, b) => {
                // Sort by created date, most recent first
                const aDate = new Date(a.created || 0).getTime();
                const bDate = new Date(b.created || 0).getTime();
                return bDate - aDate;
              })
              .map(task => (
                <TaskItem
                  key={task.key}
                  task={task}
                  isExpanded={expandedTasks.has(task.key)}
                  onToggleExpansion={toggleTaskExpansion}
                />
              ))}
          </div>
        ) : (
          <NoData
            icon={<ClockIcon />}
            title="No Tasks Found"
            description="Task execution history will appear here when operations are performed."
          />
        )}
      </Container>
    </div>
  );
};
